/**
 * @fileoverview Standalone Privacy Policy page.
 * @module features/landing/pages/PrivacyPolicyPage
 */

import { ReactElement, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'
import { PrivacyPolicyContent } from '../components/PrivacyPolicyContent'
import { Spotlight } from '@/components/ui/spotlight-new'
import PageMeta from '@/shared/components/common/PageMeta'
import type { SupportedLanguage } from '@/shared/components/common/seoUtils'

interface PrivacyPolicyPageProps {
  /** Language for this page instance */
  lang?: SupportedLanguage
}

/**
 * Standalone Privacy Policy page component.
 * Displays GDPR/RODO compliant privacy policy as a full page.
 *
 * @component
 */
export function PrivacyPolicyPage({ lang = 'en' }: PrivacyPolicyPageProps): ReactElement {
  const { t, i18n } = useTranslation('features-landing')

  // Set language based on URL
  useEffect(() => {
    if (lang && i18n.language !== lang) {
      void i18n.changeLanguage(lang)
    }
  }, [lang, i18n])

  return (
    <div className="relative min-h-svh overflow-x-hidden bg-[oklch(0.15_0_200)]">
      <PageMeta
        title={t('privacyPolicy.title')}
        description="Privacy Policy for AbyssBlock - GDPR/RODO compliant data protection information."
        canonicalPath={`/${lang}/privacy-policy`}
        lang={lang}
      />

      {/* Background Effects */}
      <div className="fixed inset-0 dot-pattern-subtle pointer-events-none" />

      {/* Spotlight Effect */}
      <div className="fixed inset-0 pointer-events-none">
        <Spotlight
          gradientFirst="radial-gradient(68.54% 68.72% at 55.02% 31.46%, hsla(210, 100%, 85%, .04) 0, hsla(210, 100%, 55%, .01) 50%, hsla(210, 100%, 45%, 0) 80%)"
          gradientSecond="radial-gradient(50% 50% at 50% 50%, hsla(210, 100%, 85%, .03) 0, hsla(210, 100%, 55%, .01) 80%, transparent 100%)"
          gradientThird="radial-gradient(50% 50% at 50% 50%, hsla(210, 100%, 85%, .02) 0, hsla(210, 100%, 45%, .01) 80%, transparent 100%)"
        />
      </div>

      {/* Main Content */}
      <main className="relative z-10 py-12 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl">
          {/* Back link */}
          <Link
            to={`/${lang}/`}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            <ArrowLeft className="size-4" />
            {lang === 'pl' ? 'Powrót do strony głównej' : 'Back to home'}
          </Link>

          {/* Title */}
          <h1 className="text-2xl font-semibold text-foreground mb-8">
            {t('privacyPolicy.title')}
          </h1>

          {/* Content */}
          <PrivacyPolicyContent />
        </div>
      </main>
    </div>
  )
}
